import MultilineTextBoxViewModel from '@/ViewModels/Controls/MultilineTextBoxViewModel';
import CheckBoxViewModel from '@/ViewModels/Controls/CheckBoxViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import WindowViewModel from '@/ViewModels/Controls/WindowViewModel';
import ButtonViewModel from '@/ViewModels/Controls/ButtonViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import * as DefaultViewExtensionInterfaces from '#/DefaultViewExtensionInterfaces';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import WindowViewModelT from '@/ViewModels/Controls/WindowViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import CheckBoxViewModelT from '@/ViewModels/Controls/CheckBoxViewModel';
import MultilineTextBoxViewModelT from '@/ViewModels/Controls/MultilineTextBoxViewModel';
import RADApplication from '@/Management/RADApplication';
import Config from '@/Management/GlobalConfiguration';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import ImageHandler from '@/Utility/ImageHandler';
import * as LocalizationHelper from '@/Utility/LocalizationHelper';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Utilities from '@/Utility/Utilities';
import Loader from '@/Utility/Loader';
import { ILanguageDTO } from "coreframetypings/dto";
import { IMLStringPopupBoxViewModelConstructorParameter } from "#/DefaultViewExtensionInterfaces";

export default class MLStringPopupBoxViewModel extends ControlViewModel {
    /**
     * Gibt an, ob die Checkbox zum Umschalten zwischen einfacher und mehrsprachiger Eingabe verwendet werden soll.
     */
    public useIsEachLanguageEqual: boolean;
    /**
     * Gibt an, ob der Text für alle Sprachen identisch ist
     */
    public isEachLanguageEqual: KnockoutObservable<boolean>;
    /**
     * Gibt an, ob der Text für alle Sprachen im Popup mit einem einzigen Control bearbeitet wird oder separat.
     */
    public window_isEachLanguageEqual: KnockoutObservable<boolean>;
    /**
     * Gibt an, ob die Eingabe in ein mehrzeiliges Textfeld erfolgt
     */
    public isMultiline: boolean;
    /**
     * Definiert den Separator-String
     */
    public separatorString: KnockoutObservable<string>;
    /**
     * Definiert den Separator-String
     */
    public ergName: string;
    /**
     * Das ViewModel für die MultilineTextBox
     */
    public multilineTextBox: MultilineTextBoxViewModelT;
    /**
     * Gibt die maximale Länge der Texte für die einzelnen Sprachen an
     */
    public maxLength: number;
    /**
     * Enthält das ViewModel für die im Popup-Fenster verwendete MultilineTextBox
     */
    public window_multilineTextBox: MultilineTextBoxViewModelT;
    /**
     * Das ViewModel für die Checkbox (PopUp)
     */
    public checkBox: CheckBoxViewModelT;
    /**
     * Das ViewModel für das Label der MultilineTextboxEachLanguageEqual
     */
    public label: LabelViewModelT;
    /**
     * Das ViewModel für den Link
     */
    public link: LinkViewModelT;
    /**
     * Das ViewModel für das Window
     */
    public window: KnockoutObservable<WindowViewModelT>;
    /**
     * Die angezeigten Buttons
     */
    public buttons: KnockoutObservableArray<ButtonViewModelT>;
    public value: KnockoutObservable<string>;
    /**
     * Liste von ViewModels für jede Sprache
     */
    public groups: KnockoutComputed<Array<GroupType>>;
    /**
     * Definiert, ob der link fokussierbar ist
     */
    public isLinkFocusable: KnockoutObservable<boolean>;
    /**
     * Definiert, ob der link fokussierbar ist
     */
    public isMultilineTextBoxFocusable: KnockoutObservable<boolean>;
    public isTranslationServiceActive: KnockoutComputed<boolean>;
    public window_translationLabel: LabelViewModelT;

    private readonly LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
    private readonly ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    private readonly WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModelT>('WindowViewModel');
    private readonly LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private readonly ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelT>('ButtonViewModel');
    private readonly CheckBoxViewModel = ComponentManager.getViewModel<typeof CheckBoxViewModelT>('CheckBoxViewModel');
    private readonly MultilineTextBoxViewModel = ComponentManager.getViewModel<typeof MultilineTextBoxViewModelT>('MultilineTextBoxViewModel');

    /**
     * Ein internes Objekt zur Verwaltung der Eingaben
     */
    private languageDict: Record<string, KnockoutObservable<string>>;

    /** Wird beim Schließen des Fensters überprüft, um zu erfahren, ob die Daten übernommen werden sollen; wird bei OK oder Abbrechen gesetzt. */
    private acceptWindowValues: boolean;

    private language: ILanguageDTO;
    private readDependency: KnockoutObservable<boolean>;

    private autoTranslation: {
        onRegistration: () => void;
        onCancellation: () => void;
    };

    constructor(params: IMLStringPopupBoxViewModelConstructorParameter) {
        super(params);

        this.languageDict = {};
        this.useIsEachLanguageEqual = this.paramOrDefault(params.useIsEachLanguageEqual, true);
        this.isEachLanguageEqual = this.observableOrDefault(params.isEachLanguageEqual, false);
        this.window_isEachLanguageEqual = this.observableOrDefault(this.isEachLanguageEqual());
        this.isMultiline = this.paramOrDefault(params.isMultiline, true);
        this.separatorString = this.observableOrDefault(params.separatorString, '');
        this.ergName = this.paramOrDefault(params.ergName, '');
        this.isLinkFocusable = this.observableOrDefault(params.isLinkFocusable, false);
        this.isMultilineTextBoxFocusable = this.observableOrDefault(params.isMultilineTextBoxFocusable, false);
        this.multilineTextBox = this.paramOrDefault(params.multilineTextBox, null);
        this.maxLength = this.paramOrDefault(params.maxLength, -1);
        this.autoTranslation = this.paramOrDefault(params.autoTranslation, { onRegistration: () => {}, onCancellation: () => {} });
        this.setupAutoTranslation();
        this.isTranslationServiceActive = this.defineComputed(() => {
            return !this.window_isEachLanguageEqual() && RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowTranslationProvider');
        });
        this.window_translationLabel = new this.LabelViewModel({
            ergName: 'MLSTRING_TRANSLATION_PROVIDER_INFO',
            isVisible: this.isTranslationServiceActive,
            parentLocator: this.locator,
            locator: 'window.translationinfo.label',
        });
        this.window_multilineTextBox = new this.MultilineTextBoxViewModel({
            isEnabled: this.isEnabled,
            isMultiline: this.isMultiline,
            isFocusable: this.isMultilineTextBoxFocusable,
            maxLength: this.maxLength,
            parentLocator: this.locator,
            locator: 'window.value.all',
            tooltip: this.multilineTextBox.tooltip
        });
        this.checkBox = this.paramOrDefault(params.checkBox, (() => {
            return new this.CheckBoxViewModel({
                isChecked: this.window_isEachLanguageEqual,
                isEnabled: this.isEnabled,
                label: new this.LabelViewModel({
                    ergName: "FOR_ALL_LANGUAGE_EQUALS",
                    parentLocator: this.locator,
                    locator: 'window.isEachLanguageEqual.label'
                }),
                parentLocator: this.locator,
                locator: 'window.isEachLanguageEqual',
                isVisible: this.useIsEachLanguageEqual
            });
        })());
        this.label = this.paramOrDefault(params.label, (() => {
            return new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'value-label',
                ergName: this.ergName
            });
        })());
        this.link = this.paramOrDefault(params.link, (() => {
            return new this.LinkViewModel({
                parentLocator: this.locator,
                locator: 'button.open-dialog',
                tooltip: new this.LabelViewModel({
                    ergName: 'MLSTRING_CONFIGURATION_TOOLTIP'
                }),
                image: new this.ImageViewModel({
                    locator: 'button.open-dialog.image',
                    source: ImageHandler.buildSource(Config.inlineImages.MLSTRING_EDIT),
                    parentLocator: this.locator,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'MLSTRING_CONFIGURATION_TOOLTIP'
                    })
                }),
                isFocusable: this.isLinkFocusable,
                isToolBarLink: true,
                onClick: () => {
                    if (!this.window() || this.window().isDisposed) {
                        this.initWindow();
                    }
                    this.window().isOpen(true);
                }
            });
        })());
        this.acceptWindowValues = false;
        this.window = this.observableOrDefault(params.window, null);
        this.buttons = this.observableOrDefault(params.buttons, (() => {
            return [
                new this.ButtonViewModel({
                    parentLocator: this.locator,
                    locator: 'window.button.ok',
                    isPrimaryButton: true,
                    image: new this.ImageViewModel({
                        parentLocator: this.locator,
                        locator: 'window.button.ok.image',
                        source: Config.inlineImages.WINDOW_OK,
                        alternativeText: new this.LabelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new this.LabelViewModel({
                        parentLocator: this.locator,
                        locator: 'window.button.ok.label',
                        ergName: 'OKAY'
                    }),
                    onClick: () => {
                        this.acceptWindowValues = true;
                        this.window().isOpen(false);
                        this.window().dispose();
                    }
                }),
                new this.ButtonViewModel({
                    parentLocator: this.locator,
                    locator: 'window.button.cancel',
                    image: new this.ImageViewModel({
                        parentLocator: this.locator,
                        locator: 'window.button.cancel.image',
                        source: Config.inlineImages.WINDOW_CANCEL,
                        alternativeText: new this.LabelViewModel({
                            ergName: 'CANCEL'
                        })
                    }),
                    label: new this.LabelViewModel({
                        parentLocator: this.locator,
                        locator: 'window.button.cancel.label',
                        ergName: 'CANCEL'
                    }),
                    onClick: () => {
                        this.acceptWindowValues = false;
                        this.window().isOpen(false);
                        this.window().dispose();
                    }
                })
            ];
        })()) as KnockoutObservableArray<ButtonViewModelT>;
        // Die Language-Observables sollen hier schon erzeugt werden, damit Knockout die Abhängigkeit der Read-Methode
        // zu diesen erkennt; ansonsten würden sie nämlich erst in der read-Methode erzeugt werden und dort nicht als Abhängigkeit aufgenommen werden.
        // Dadurch wird this.value auch direkt aktualisiert, wenn sich einer der sprachspezifischen Werte ändert
        for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
            this.language = RADApplication.getInstance().configuration.languages[i];
            this.getLanguageObservable(this.language.iso6391);
        }
        // Dient dem Setzen des Wertes der Haupt-TextBox, ohne dass ggf. initiale null-Werte durch einen leeren String ersetzt werden,
        // was zu unerwünschten Benachrichtungen via dessen this.value-Observable führen würde
        // Damit wir beim Schreiben des Gesamtwertes via write nicht temporäre Werte an die Subscriber (insbesondere this.value selbst)
        // übermitteln, können wir hiermit die read-Methode temporär ausschalten und dann manuell triggern.
        this.readDependency = this.observableOrDefault(true).extend({ notify: 'always' });
        this.readDependency.allowRead = true;
        this.readDependency.cacheValue = null;
        this.value = this.defineComputed({
            read: () => {
                this.readDependency(); // Knockout die Abhängigkeit erkennen lassen, um sie triggern zu können
                let value = this.multilineTextBox.value();
                let isEachLanguageEqual = this.isEachLanguageEqual();
                let separatorString = this.separatorString();
                let result;
                if (isEachLanguageEqual === true) {
                    result = value;
                } else {
                    let parts = [];
                    for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                        let language = RADApplication.getInstance().configuration.languages[i];
                        let langValue = this.getLanguageValue(language.iso6391);
                        if (langValue !== '')
                            parts.push(language.iso6391 + ':' + langValue);
                    }
                    if (parts.length === 0) {
                        result = '';
                    }
                    else {
                        result = separatorString + parts.join(separatorString);
                    }
                }
                if (!this.readDependency.allowRead) { // Ist das ein durch write/knockout INDIREKT getriggerter Aufruf? Wenn ja, dann noch alten Wert zurückgeben.
                    return this.readDependency.cacheValue;
                }
                this.readDependency.cacheValue = result; // Wert merken, um ihn in Schreibphasen zurückgeben zu können
                return result;
            },
            write: (value) => {
                value = value || '';
                let isEachLanguageEqual = this.isEachLanguageEqual();
                let separatorString = this.separatorString();
                if (isEachLanguageEqual === true) {
                    this.setTextBoxValue(value);
                    for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                        let language = RADApplication.getInstance().configuration.languages[i];
                        this.setLanguageValue(value, language.iso6391);
                    }
                } else {
                    let languageValues = value.split(separatorString);
                    for (let i = languageValues.length - 1; i >= 0; i--) {
                        if (languageValues[i] == null || languageValues[i] === '') {
                            languageValues.splice(i, 1);
                        }
                    }
                    // Da read von den einzelnen Sprachwerten abhängt, schalten wir die Evaluierung hier temporär aus.
                    // Ansonsten würden schon durch das initiale Setzen den Subsribern von this.params.value falsche Werte
                    // übermittelt werden (dieses Observable wird mit this.params.value synchronisiert).
                    this.readDependency.allowRead = false;
                    // Es dürfen nur die Werte beachtet werden, die vom Server als Derived Änderungen zurück kommen
                    // Das heißt, alle anderen müssen wieder zurück auf '' gesetzt werden.
                    if (languageValues && languageValues.length > 0 && languageValues.length < RADApplication.getInstance().configuration.languages.length) {
                        for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                            let lan = RADApplication.getInstance().configuration.languages[i].iso6391;
                            let regex = new RegExp(lan + ':', "g");
                            let contains = false;
                            for (let j = 0; j < languageValues.length; j++) {
                                contains = contains || languageValues[j].match(regex) ? true : false;
                            }
                            if (!contains)
                                this.setLanguageValue('', lan);
                        }
                    }
                    for (let i = 0; i < languageValues.length; i++) {
                        let languageValue = languageValues[i]; // like: DE:example-text
                        let index = languageValue.indexOf(":");
                        let mlValue = languageValue.substring(index + 1, languageValue.length); // like: 'example-text'
                        let mlLang = languageValue.substring(0, index); // like: 'DE'
                        this.setLanguageValue(mlValue, mlLang);
                    }
                    // Die TextBox soll synchron mit dem Wert der aktuellen Sprache sein
                    let currentLanguage = ResourceManager.getInstance().getLanguage().iso6391;
                    let currentLanguageValue = this.getLanguageValue(currentLanguage);
                    this.setTextBoxValue(currentLanguageValue);
                    // Wert des Observables soll jetzt aktualisiert werden (in Knockout gespeichertes latestValue ist nicht automatisch der an write übergebene Wert!)
                    this.readDependency.allowRead = true;
                    this.readDependency(true);
                }
            }
        }, params.value);
        this.groups = this.defineComputed({
            read: () => {
                let result = [];
                // Beim Wechsel der aktuellen Sprache werden auch die ViewModels neu aufgebaut
                let currentLanguage = ResourceManager.getInstance().getLanguage().iso6391;
                // Die Abhängigkeit besteht nur zur Sprache; daher alle anderen Abhängigkeiten ausschalten:
                ko.ignoreDependencies(() => {
                    for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                        let language = RADApplication.getInstance().configuration.languages[i];
                        let group = this.languageGroup(language);
                        result.push(group);
                    }
                });
                return result;
            },
            deferEvaluation: true
        });
        this.defineSubscription(this.multilineTextBox.value, (value) => {
            let isEachLanguageEqual = this.isEachLanguageEqual();
            if (isEachLanguageEqual === true) {
                for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                    let language = RADApplication.getInstance().configuration.languages[i];
                    this.setLanguageValue(value, language.iso6391);
                }
            } else {
                let currentLanguage = ResourceManager.getInstance().getLanguage().iso6391;
                this.setLanguageValue(value, currentLanguage);
            }
        });
    }
    /**
     * Gibt KeyValue-Paare zwischen Sprache und Übersetzung zurück (Übersetzungen sind Observables)
     */
    public getValues(): { [key: string]: KnockoutObservable<string> } {
        return this.languageDict;
    }
    /**
     * Gibt KeyValue-Paare zwischen Sprache und Übersetzung zurück
     */
    public getPlainValues(): { [key: string]: string } {
        let plainValues = {};
        for (let lang in this.languageDict) {
            if (this.languageDict.hasOwnProperty(lang)) {
                plainValues[lang] = this.languageDict[lang]();
            }
        }
        return plainValues;
    }
    
    protected disposeInternal(): void {
        this.disposeObject(this.groups);
        super.disposeInternal();
    }
    
    public resetValue(): any {
        this.separatorString(null);
        this.value(null);
    }
    private initWindow(): any {
        this.window(new this.WindowViewModel({
            parentLocator: this.locator,
            locator: 'window',
            width: 550,
            isModal: true,
            isOpen: false,
            allowClose: false,
            headline: new this.LabelViewModel({
                ergName: 'MULTILINGUAL_INPUT'
            }),
            // Daten in die Controls im Fenster kopieren
            onOpen: () => {
                this.window_isEachLanguageEqual(this.isEachLanguageEqual());
                this.window_multilineTextBox.value(this.multilineTextBox.value());
                let groups = this.groups();
                for (let i = 0; i < groups.length; i++) {
                    let group = groups[i];
                    group.multilineTextBox.value(group.liveValue());
                }

                this.autoTranslation.onCancellation();
            },
            // Daten aus den Controls im Fenster übernehmen
            onClose: () => {
                this.link.hasFocus(true);
                if (!this.acceptWindowValues) return;
                this.acceptWindowValues = false; // Falls das Fenster beim nächsten mal nicht über einen Button geschlossen wird, so soll nichts passieren; daher wird hier false gesetzt
                this.isEachLanguageEqual(this.window_isEachLanguageEqual());
                if (this.isEachLanguageEqual()) {
                    this.multilineTextBox.value(this.window_multilineTextBox.value());
                }
                else {
                    let groups = this.groups();
                    let currentLanguage = ResourceManager.getInstance().getLanguage().iso6391;
                    for (let i = 0; i < groups.length; i++) {
                        let group = groups[i];
                        // Wenn der Wert für die Sprache geändert wird, die auch in der TextBox verwendet wird, müssen wir diese ebenfalls aktualisieren
                        if (group.language == currentLanguage) {
                            this.setTextBoxValue(group.multilineTextBox.value());
                        }
                        group.liveValue(group.multilineTextBox.value());
                    }
                }
                this.window().dispose();
            }
        }));
    }
    private setTextBoxValue(value: any): any {
        if (value == null) {
            value = '';
        }
        let currentValue = this.multilineTextBox.value();
        if (currentValue == null && value == '') {
            return;
        }
        this.multilineTextBox.value(value);
    }
    private languageGroup(language: any): any {
        return {
            language: language.iso6391,
            liveValue: this.getLanguageObservable(language.iso6391),
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'window.value.' + language.iso6391 + '.label',
                caption: LocalizationHelper.getLanguageFullName(language.iso6391)
            }),
            multilineTextBox: new this.MultilineTextBoxViewModel({
                parentLocator: this.locator,
                locator: 'window.value.' + language.iso6391,
                isMultiline: this.isMultiline,
                value: "", // Wert wird in this.window.onOpen von liveValue hier hin übertragen und in .onClose zurück
                maxLength: this.maxLength,
                isFocusable: this.isMultilineTextBoxFocusable,
                isEnabled: this.isEnabled,
                tooltip: this.multilineTextBox.tooltip,
                onSubmit: () => {
                    if (this.window().isOpen()) {
                        this.window().isOpen(false);
                    }
                },
                language: language.iso6391
            }),
            flag: new this.ButtonViewModel({
                parentLocator: this.locator,
                locator: 'window.value.' + language.iso6391 + '.button',
                isEnabled: this.definePureComputed({
                    read: () => {
                        return this.isEnabled() && this.isTranslationServiceActive() && _.keys(this.getFilledPlainValuesFromWindow()).indexOf(language.iso6391) > -1;
                    }
                }),
                isFocusable: this.isTranslationServiceActive,
                image: new this.ImageViewModel({
                    parentLocator: this.locator,
                    locator: 'window.value.' + language.iso6391 + '.image',
                    // Das vermeidet einen überflüssigen Request je Flagge; der Browser cached das Bild über die URL.
                    source: ImageHandler.convertSource(language.iconUrl),
                    isConvertedSource: true,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LOCALIZATION.LANGUAGE_FLAG_ERGNAME'
                    })
                }),
                onClick: (openInNewWindow, event) => {
                    if (!this.isTranslationServiceActive())
                        return;
                    // exclusive or
                    if (event.ctrlKey == event.shiftKey)
                        return;
                    Loader.getInstance().show();
                    RADEnvironmentProxy.getInstance().translate({
                        originLanguage: language.iso6391,
                        existingTranslations: this.getFilledPlainValuesFromWindow(),
                        overrideTranslations: event.ctrlKey
                    }).then((result) => {
                        if (!this.validateStatus(result))
                            return;
                        let translated = result.returnValue;
                        this.setLanguageValuesInWindow(translated);
                    }).always(() => {
                        Loader.getInstance().hide();
                    });
                }
            })
        };
    }
    /**
     * Hilfsfunktion zum Setzen eines Sprachwerts
     */
    private setLanguageValue(value: any, iso6391: any): any {
        if (iso6391 in this.languageDict) {
            this.languageDict[iso6391](value);
        } else {
            this.languageDict[iso6391] = this.observableOrDefault(value);
        }
    }
    /**
     * Hilfsfunktion zum Setzen mehrerer Sprachwerte (im Fenster)
     */
    private setLanguageValuesInWindow(dict: any): any {
        let groups = this.groups();
        _.keys(dict).forEach((key) => {
            let group = _.find(groups, (group) => { return group.language == key; });
            if (group != null) {
                group.multilineTextBox.value(dict[key]);
            }
        });
    }
    /**
     * Hilfsfunktion zum Auslesen eines Sprach-Observables
     */
    private getLanguageObservable(iso6391: any): any {
        if (iso6391 in this.languageDict) {
            return this.languageDict[iso6391];
        } else {
            let value = '';
            this.languageDict[iso6391] = this.observableOrDefault(value);
            return value;
        }
    }
    /**
     * Hilfsfunktion zum Auslesen eines Sprachwerts
     */
    private getLanguageValue(iso6391: any): any {
        if (iso6391 in this.languageDict) {
            return this.languageDict[iso6391]();
        } else {
            let value = '';
            this.languageDict[iso6391] = this.observableOrDefault(value);
            return value;
        }
    }
    private getFilledPlainValuesFromWindow(): any {
        let groups = this.groups();
        let filled = {};
        groups.forEach((group) => {
            if (group.multilineTextBox.isDisposed)
                return;
            let value = group.multilineTextBox.value();
            if (!Utilities.isNullOrUndefinedOrBlank(value))
                filled[group.language] = value;
        });
        return filled;
    }

    private setupAutoTranslation() {
        if (!this.multilineTextBox)
            return;

        const originalOnKeyDown = this.multilineTextBox.onKeyDown;
        this.multilineTextBox.onKeyDown = (keyCode) => {
            if (originalOnKeyDown)
                originalOnKeyDown(keyCode);

            if (!this.multilineTextBox?.isEnabled())
                return;

            this.autoTranslation.onRegistration();
        };
    }
}
interface GroupType {
    language: string,
    liveValue: KnockoutObservable<string>,
    label: LabelViewModelT,
    multilineTextBox: MultilineTextBoxViewModelT,
    image: ImageViewModelT;
}

ComponentManager.setViewModel('MLStringPopupBoxViewModel', MLStringPopupBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');